import { ColorProofer } from '@flobi/artwork-core';
import { createContext, useContext, useEffect, useState } from 'react';
// From the engine package: the press profile the render service writes a print PDF with.
import pressProfile from '@flobi/artwork-core/icc/ISOcoated_v2_eci.icc?url';
// Little-CMS's WebAssembly, served by Vite beside the app; the engine only loads it when a
// CMYK file asks.
import lcmsWasm from 'lcms-wasm/dist/lcms.wasm?url';

/**
 * The press a CMYK file is made for (ISO Coated v2, FOGRA39), opened once per session.
 *
 * Nothing is fetched until the first CMYK file needs it: an RGB file pays nothing. A
 * failed open is forgotten, so the next CMYK file (or the next switch) tries again.
 */
let opening: Promise<ColorProofer> | null = null;
let opened: ColorProofer | null = null;

export function openPress(): Promise<ColorProofer> {
  opening ??= (async () => {
    const response = await fetch(pressProfile);
    if (!response.ok) throw new Error(`Could not load the press profile: ${response.status}`);
    const icc = new Uint8Array(await response.arrayBuffer());
    const proofer = await ColorProofer.open(icc, { locateFile: () => lcmsWasm });
    opened = proofer;
    return proofer;
  })().catch((error: unknown) => {
    opening = null;
    throw error;
  });
  return opening;
}

/** The press, if it has been opened this session. */
export function pressIfOpen(): ColorProofer | null {
  return opened;
}

/** The size of the press table the canvas samples: 33 steps a side, as print tools use. */
export const PRESS_LUT_SIZE = 33;
const luts = new WeakMap<ColorProofer, Uint8Array>();

/** The press as a table, worked out once per press (the canvas re-makes its pass from it). */
export function pressLut(proofer: ColorProofer): Uint8Array {
  let lut = luts.get(proofer);
  if (!lut) {
    lut = proofer.lut(PRESS_LUT_SIZE);
    luts.set(proofer, lut);
  }
  return lut;
}

/**
 * The press, to the chrome: set in a CMYK file once it has loaded, null otherwise. Every
 * well, swatch and picker reads it, so they show colours as they print and pick in ink.
 */
export const PressContext = createContext<ColorProofer | null>(null);

export function usePress(): ColorProofer | null {
  return useContext(PressContext);
}

/** The press for a file in this colour mode: null in RGB, and until it has loaded. */
export function useProofer(mode: 'rgb' | 'cmyk'): ColorProofer | null {
  const [proofer, setProofer] = useState<ColorProofer | null>(() => (mode === 'cmyk' ? pressIfOpen() : null));
  useEffect(() => {
    if (mode !== 'cmyk') {
      setProofer(null);
      return;
    }
    let live = true;
    openPress()
      .then((press) => live && setProofer(press))
      .catch((error: unknown) => console.error('The press profile could not be opened', error));
    return () => {
      live = false;
    };
  }, [mode]);
  return mode === 'cmyk' ? proofer : null;
}
